import { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Check } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { useLang, UI } from "@/i18n/lang";
import { BRAND, whatsappLink } from "@/content/site";
import { WhatsAppIcon } from "@/components/WhatsAppFab";
import { Reveal } from "@/lib/motion";
import { Seo, ORIGIN } from "@/components/Seo";
import { useLocaleRoute, localizePath, coreAlternates } from "@/i18n/routes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full bg-transparent border-b border-border py-4 text-lg text-ink placeholder:text-stone/40 focus:border-ink outline-none transition-colors duration-300";

export default function Contact({ locale = "de" }) {
  const { t } = useLang();
  useLocaleRoute(locale, localizePath("/contact", locale === "de" ? "en" : "de"));
  const [form, setForm] = useState({ name: "", email: "", build_type: "", budget: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success(t(UI.contact.success));
      setForm({ name: "", email: "", build_type: "", budget: "", message: "" });
    } catch (err) {
      toast.error(t(UI.contact.error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title={UI.seo.contact.title[locale]}
        description={UI.seo.contact.desc[locale]}
        path={localizePath("/contact", locale)}
        lang={locale}
        image={`${ORIGIN}/og-contact.jpg`}
        alternates={coreAlternates("/contact")}
      />
      <section className="pt-36 md:pt-44 pb-16">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <Reveal>
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-bronze">— {t(UI.nav.contact)}</span>
                <h1 className="mt-6 font-serif text-5xl md:text-6xl lg:text-7xl tracking-tight text-ink leading-[0.98]">
                  {t(UI.contact.title)}
                </h1>
                <p className="mt-6 max-w-sm text-base text-stone">{t(UI.contact.intro)}</p>

                <div className="mt-12 space-y-5">
                  {BRAND.email && (
                    <a
                      href={`mailto:${BRAND.email}`}
                      data-testid="contact-email-link"
                      className="group flex items-center justify-between border-b border-border pb-3"
                    >
                      <span className="font-mono text-[11px] uppercase tracking-widest text-stone">Email</span>
                      <span className="text-base text-ink transition-colors group-hover:text-bronze break-all">{BRAND.email}</span>
                    </a>
                  )}
                  {BRAND.whatsapp && (
                    <a
                      href={whatsappLink()}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-whatsapp-link"
                      className="group flex items-center justify-between border-b border-border pb-3"
                    >
                      <span className="font-mono text-[11px] uppercase tracking-widest text-stone">WhatsApp</span>
                      <span className="flex items-center gap-2 text-base text-ink transition-colors group-hover:text-bronze">
                        {BRAND.whatsappDisplay}
                        <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </span>
                    </a>
                  )}
                  {BRAND.address && (
                    <div data-testid="contact-impressum" className="pt-4">
                      <p className="font-mono text-[11px] uppercase tracking-widest text-stone mb-2">{t(UI.footer.impressum)}</p>
                      <address className="not-italic text-sm leading-relaxed text-ink/70">
                        {BRAND.owner}<br />
                        {BRAND.address.street}<br />
                        {BRAND.address.city}
                      </address>
                    </div>
                  )}
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-6 lg:col-start-7">
              {done ? (
                <Reveal>
                  <div data-testid="contact-success" className="border border-border p-10 md:p-14 text-center flex flex-col items-center">
                    <div className="w-14 h-14 rounded-full bg-bronze flex items-center justify-center mb-6">
                      <Check size={24} className="text-paper" />
                    </div>
                    <p className="font-serif text-3xl text-ink">{t(UI.contact.success)}</p>
                    <button
                      onClick={() => setDone(false)}
                      className="mt-8 font-mono text-[12px] uppercase tracking-widest text-stone hover:text-ink transition-colors"
                    >
                      ← {t(UI.cta.sendAnother)}
                    </button>
                  </div>
                </Reveal>
              ) : (
                <Reveal>
                  <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
                    <div>
                      <label className="font-mono text-[11px] uppercase tracking-widest text-bronze">{t(UI.contact.name)} *</label>
                      <input data-testid="contact-name" value={form.name} onChange={update("name")} required className={field} placeholder={t(UI.contact.name)} />
                    </div>
                    <div>
                      <label className="font-mono text-[11px] uppercase tracking-widest text-bronze">{t(UI.contact.email)} *</label>
                      <input data-testid="contact-email" type="email" value={form.email} onChange={update("email")} required className={field} placeholder="name@email.com" />
                    </div>
                    <div>
                      <label className="font-mono text-[11px] uppercase tracking-widest text-bronze">{t(UI.contact.build)}</label>
                      <select data-testid="contact-build" value={form.build_type} onChange={update("build_type")} className={`${field} cursor-pointer`}>
                        <option value="">{t(UI.contact.budgetPlaceholder)}</option>
                        {t(UI.contact.buildOptions).map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="font-mono text-[11px] uppercase tracking-widest text-bronze">{t(UI.contact.budget)}</label>
                      <select data-testid="contact-budget" value={form.budget} onChange={update("budget")} className={`${field} cursor-pointer`}>
                        <option value="">{t(UI.contact.budgetPlaceholder)}</option>
                        {UI.contact.budgetOptions.map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="font-mono text-[11px] uppercase tracking-widest text-bronze">{t(UI.contact.message)} *</label>
                      <textarea data-testid="contact-message" value={form.message} onChange={update("message")} required rows={4} className={`${field} resize-none`} placeholder="…" />
                    </div>

                    <button
                      type="submit"
                      data-testid="contact-submit"
                      disabled={loading}
                      className="group inline-flex items-center gap-2 rounded-full bg-bronze text-paper px-9 py-4 text-sm font-semibold tracking-wide hover:bg-[#f0be6e] transition-colors duration-300 disabled:opacity-60"
                    >
                      {loading ? t(UI.cta.sending) : t(UI.cta.send)}
                      <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                  </form>
                </Reveal>
              )}
              {BRAND.whatsapp && <WhatsAppBox />}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const WhatsAppBox = () => {
  const { t } = useLang();
  const [msg, setMsg] = useState("");
  const open = () => window.open(whatsappLink(msg), "_blank", "noopener,noreferrer");
  return (
    <Reveal>
      <div data-testid="contact-whatsapp" className="mt-8 border border-border p-8 md:p-10">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full text-white" style={{ backgroundColor: "#25D366" }}>
            <WhatsAppIcon size={20} />
          </span>
          <div>
            <p className="font-serif text-xl text-ink leading-tight">{t(UI.whatsapp.cardTitle)}</p>
            <p className="text-sm text-stone">{t(UI.whatsapp.cardIntro)}</p>
          </div>
        </div>
        <textarea
          data-testid="whatsapp-message"
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          rows={3}
          placeholder={t(UI.whatsapp.placeholder)}
          className={`${field} resize-none mt-6`}
        />
        <button
          type="button"
          data-testid="whatsapp-send"
          onClick={open}
          className="mt-5 inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.03]"
          style={{ backgroundColor: "#25D366" }}
        >
          <WhatsAppIcon size={18} />
          {t(UI.whatsapp.send)}
        </button>
      </div>
    </Reveal>
  );
};
